/**
 * Camada de dados do Caminho no app.
 *
 * NÃO EXISTE LISTENER AQUI, e é deliberado — mesma decisão, pelo mesmo motivo,
 * do cabeçalho de `moedaService.js`. Um `onSnapshot` em CAMINHO/ATUAL para
 * manter a trilha viva pareceria óbvio e seria uma leitura por sessão aberta,
 * por dispositivo, o dia inteiro. Foi assim que o cache warmer chegou a 20,7M
 * leituras/dia.
 *
 * O caminho muda quando a PRÓPRIA PESSOA age, e quem age recebe o estado novo
 * na resposta da callable. Então:
 *
 *   1. uma leitura quando a aba do Caminho monta (e só a primeira vez na
 *      sessão — o documento fica em memória);
 *   2. o retorno das callables, que já traz o nó atualizado;
 *   3. `invalidar()` quando algo fora daqui muda o caminho (replano da Vita).
 *
 * Sem cache em localStorage, ao contrário do saldo da moeda: o caminho é
 * grande (~14 KB) e um caminho velho na tela é pior que meio segundo de
 * espera — mostraria um dia de treino que a Vita já trocou.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import callFirebaseFunction from '../../utils/firebaseFunctionCaller';
import { aplicarGanho } from '../../services/moedaService';
import tipo from './motor/tipo';
import { carregarTipoCaminho } from './caminhoConfig';

/** Documento em memória, por uid. Some quando a aba fecha, e tudo bem. */
let cache = { uid: null, caminho: null };

const documentoDoTipo = (tipoCaminho) =>
  (tipo.ehCaminhoEstudo(tipoCaminho) ? 'ESTUDO' : 'ATUAL');

const refDoCaminho = (uid, tipoCaminho) =>
  doc(db, 'PACIENTE', uid, 'CAMINHO', documentoDoTipo(tipoCaminho));

// O Firebase Callable não permite cabeçalhos arbitrários no protocolo. A
// capacidade segue no payload; clientes antigos não a enviam e o servidor os
// mantém em `CAMINHO/ATUAL`.
const comContratoCaminho = (dados = {}) => ({
  ...dados,
  CONTRATO_CAMINHO: tipo.CONTRATO_CAMINHO_ESTUDO,
});

/* ── QUEM PRECISA SABER QUE O CAMINHO MUDOU ────────────────────────────────
   `useCaminho` é usado em mais de um lugar ao mesmo tempo: a trilha e a
   sub-tela do nó têm, cada uma, a sua instância, com estado próprio. Esquecer o
   cache do módulo não faz nenhuma delas buscar de novo.

   Era isso que fazia a trilha continuar pedindo o Mapa depois de ele ter sido
   feito, e as casas de estudo só aparecerem quando o app era reaberto: a
   sub-tela recarregava a SUA cópia e voltava, e a trilha atrás continuava
   exibindo a de antes. */
let versao = 0;
const ouvintes = new Set();

/** Esquece o que está em memória e avisa quem estiver lendo. */
export function invalidar() {
  cache = { uid: null, caminho: null };
  versao += 1;
  for (const ouvinte of ouvintes) ouvinte();
}

/** Assina as invalidações. Devolve o cancelamento. */
export function assinar(ouvinte) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/** Um número que muda a cada invalidação. Para `useSyncExternalStore`. */
export function versaoAtual() {
  return versao;
}

/* Folga entre reconferências de um caminho sem plano (ver carregarCaminho). */
const FOLGA_RECONFERENCIA_MS = 10 * 60 * 1000;
let ultimaReconferencia = { uid: null, em: 0 };
function podeReconferir(uid) {
  const agora = Date.now();
  if (ultimaReconferencia.uid === uid && agora - ultimaReconferencia.em < FOLGA_RECONFERENCIA_MS) {
    return false;
  }
  ultimaReconferencia = { uid, em: agora };
  return true;
}

/** O que está em memória agora, sem ir ao servidor. */
export function caminhoEmCache(uid) {
  return cache.uid === uid ? cache.caminho : null;
}

function guardar(uid, caminho) {
  cache = { uid, caminho };
  return caminho;
}

/**
 * O caminho da pessoa, criando um se ainda não existir.
 *
 * A ORDEM IMPORTA: lê primeiro, gera só se faltar. Chamar `gerarCaminho`
 * direto seria uma invocação de função por abertura de aba — e a callable já
 * é idempotente, mas cobrar uma execução para descobrir isso é desperdício
 * que escala com a base.
 *
 * @param {string} uid dono
 * @param {object} [opcoes] {forcar}
 * @returns {Promise<object|null>} documento do caminho
 */
export async function carregarCaminho(uid, { forcar = false } = {}) {
  if (!uid) return null;
  if (!forcar) {
    const emMemoria = caminhoEmCache(uid);
    if (emMemoria) return emMemoria;
  }

  const tipoCaminho = await carregarTipoCaminho({ forcar });
  const snap = await getDoc(refDoCaminho(uid, tipoCaminho));
  if (snap.exists()) {
    const doc = snap.data();
    /* A trilha educacional é derivada do catálogo. Uma sincronização por nova
       sessão é o que faz uma lição recém-publicada entrar sem cron ou migration;
       em falha de rede, o documento já lido continua perfeitamente utilizável. */
    if (tipo.ehCaminhoEstudo(tipoCaminho)) {
      try {
        const r = await callFirebaseFunction(
          'gerarCaminho', comContratoCaminho({ tipoCaminho }),
        );
        if (r && r.caminho) return guardar(uid, r.caminho);
      } catch (e) {
        console.warn('[caminho] sincronização do catálogo falhou:', e?.message);
      }
      return guardar(uid, doc);
    }
    /* Caminho que NASCEU SEM PLANO DE TREINO (`PLANO_TREINO_REF` vazio): é a
       trilha só de descanso + lições de quem abriu a aba antes de a IA
       terminar o treino. O servidor sabe replanejar isso — mas só quando o
       callable é invocado, e este ramo devolvia o documento direto, então a
       reconferência nunca acontecia. Uma invocação por abertura (com folga de
       10 min) enquanto a trilha estiver sem plano; assim que o treino existir
       ela volta com as casas certas. */
    if (!tipo.ehCaminhoEstudo(tipoCaminho) && !doc?.PLANO_TREINO_REF && podeReconferir(uid)) {
      try {
        const r = await callFirebaseFunction(
          'gerarCaminho', comContratoCaminho({ tipoCaminho }),
        );
        if (r && r.caminho) return guardar(uid, r.caminho);
      } catch (e) {
        console.warn('[caminho] reconferência sem plano falhou:', e?.message);
      }
    }
    return guardar(uid, doc);
  }

  /* SEM DESESTRUTURAR `data`: `callFirebaseFunction` JÁ devolve `res.data`.
     Tirar `{ data }` daqui pegava `undefined`, o `guardar` gravava null, e a aba
     ficava no spinner para sempre — embora o caminho tivesse sido gerado e
     gravado. Trocar de aba remontava o componente, o `getDoc` acima encontrava o
     documento e a trilha aparecia: o sintoma era "só aparece se eu sair e
     voltar", que parece problema de renderização e não de leitura.

     Todos os outros chamadores deste arquivo já usam o retorno direto. */
  const resposta = await callFirebaseFunction(
    'gerarCaminho', comContratoCaminho({ tipoCaminho }),
  );
  return guardar(uid, (resposta && resposta.caminho) || null);
}

/** Plano novo só invalida o caminho legado. A trilha educacional não depende dele. */
export async function invalidarPorPlanoGerado() {
  const tipoCaminho = await carregarTipoCaminho();
  if (!tipo.ehCaminhoEstudo(tipoCaminho)) invalidar();
}

/** Marca o anúncio do Caminho educacional como visto no documento do usuário. */
export async function marcarModalCaminhoEstudoVisto(uid) {
  const resposta = await callFirebaseFunction(
    'marcarModalCaminhoEstudoVisto', comContratoCaminho(),
  );
  const atual = caminhoEmCache(uid);
  if (atual) guardar(uid, { ...atual, MODAL_CAMINHO_ESTUDO_VISTO: true });
  return resposta || {};
}

/**
 * Conclui um nó e devolve o que ele pagou.
 *
 * O documento em memória é atualizado com o que voltou, em vez de reler: o
 * servidor acabou de gravar, e uma segunda leitura só confirmaria o que ele já
 * disse.
 *
 * @param {string} uid dono
 * @param {object} params {data, acertos, total}
 * @returns {Promise<object>} {jaEstava, premio, xp, moeda, saldo}
 */
export async function concluirNo(uid, { data, acertos = null, total = null }) {
  const resposta = await callFirebaseFunction(
    'concluirNoDoCaminho',
    comContratoCaminho({ data, acertos, total }),
  );
  const r = resposta || {};

  const atual = caminhoEmCache(uid);
  if (atual && atual.CASAS && atual.CASAS[data]) {
    const marcado = {
      ...atual.CASAS[data],
      CONCLUIDO_EM: Date.now(),
      ABERTO_EM: Date.now(),
      PREMIO: r.premio || atual.CASAS[data].PREMIO,
      XP: r.xp || 0,
      MOEDA: r.moeda || 0,
    };
    guardar(uid, { ...atual, CASAS: { ...atual.CASAS, [data]: marcado } });
  } else if (atual && atual.NOS && atual.NOS[data]) {
    // Carimbo LOCAL: o servidor usa serverTimestamp, que não volta na resposta.
    // O que importa para a tela é que o nó saia de "atual" e vire "concluído";
    // o valor exato do carimbo chega na próxima leitura de verdade.
    const marcado = { ...atual.NOS[data] };
    if (marcado.T === 'bau') marcado.ABERTO_EM = Date.now();
    else if (marcado.T === 'checkpoint') marcado.FOTO_EM = Date.now();
    else marcado.FEITO_EM = Date.now();
    if (r.premio) marcado.PREMIO = r.premio;
    if (acertos != null) marcado.ACERTOS = acertos;
    if (total != null) marcado.TOTAL = total;
    marcado.XP = r.xp || 0;
    marcado.MOEDA = r.moeda || 0;

    guardar(uid, { ...atual, NOS: { ...atual.NOS, [data]: marcado } });
  }

  // Mesmo caso da lição (relato k8xy99): aula e baú do Caminho pagam moeda e
  // devolvem `saldo`, mas o saldo da TopBar só se move por `aplicarGanho`.
  const ganho = Number(r.moeda) || 0;
  if (ganho > 0) {
    aplicarGanho({
      creditou: true,
      valorFinal: ganho,
      valorBase: ganho,
      saldoApos: r.saldo ?? null,
    });
  }

  return r;
}

/**
 * Adia o checkpoint de uma fase. O caminho NÃO trava — ver a decisão 2 e o
 * cabeçalho de `adiarCheckpoint` na callable.
 *
 * @param {string} uid dono
 * @param {object} params {fase, adiamento}
 * @returns {Promise<object>} {checkpoint}
 */
export async function adiarCheckpoint(uid, { fase, adiamento = 'DOIS_DIAS' }) {
  const resposta = await callFirebaseFunction(
    'adiarCheckpoint',
    comContratoCaminho({ fase, adiamento }),
  );
  const r = resposta || {};

  const atual = caminhoEmCache(uid);
  if (atual && Array.isArray(atual.FASES) && r.checkpoint) {
    const FASES = atual.FASES.map((f) =>
      (f && f.N === fase ? { ...f, CHECKPOINT: r.checkpoint } : f));
    guardar(uid, { ...atual, FASES });
  }

  return r;
}

export default carregarCaminho;
